import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};

// Mock exchange rates (you can integrate a real API later)
const exchangeRates = {
  USD: { symbol: '$', rate: 1, name: 'US Dollar' },
  EUR: { symbol: '€', rate: 0.92, name: 'Euro' },
  GBP: { symbol: '£', rate: 0.79, name: 'British Pound' },
  INR: { symbol: '₹', rate: 83.12, name: 'Indian Rupee' },
  CAD: { symbol: 'C$', rate: 1.36, name: 'Canadian Dollar' },
  AUD: { symbol: 'A$', rate: 1.53, name: 'Australian Dollar' },
  JPY: { symbol: '¥', rate: 149.50, name: 'Japanese Yen' },
};

export const CurrencyProvider = ({ children }) => {
  const { userDoc, updateUser } = useAuth();
  const [currency, setCurrencyState] = useState('USD');

  useEffect(() => {
    const next = String(userDoc?.currency || 'USD');
    if (exchangeRates[next] && next !== currency) {
      setCurrencyState(next);
    }
    if (!exchangeRates[next] && currency !== 'USD') {
      setCurrencyState('USD');
    }
  }, [userDoc?.currency]);

  const setCurrency = async (nextCurrency) => {
    const code = String(nextCurrency || '').trim();
    if (!exchangeRates[code]) return;
    setCurrencyState(code);
    if (typeof updateUser === 'function') {
      try {
        await updateUser({ currency: code });
      } catch {
        // ignore
      }
    }
  };

  const getCurrencyMeta = (code) => {
    const k = String(code || '').trim();
    return exchangeRates[k] || exchangeRates.USD;
  };

  const formatMoney = (amount, code = currency, showSymbol = true) => {
    if (amount === null || amount === undefined || amount === '') return showSymbol ? 'Not Available' : '';
    const n = Number(amount);
    if (!Number.isFinite(n)) return showSymbol ? 'Not Available' : '';

    const meta = getCurrencyMeta(code);
    const symbol = meta.symbol;

    let formattedPrice;
    if (String(code) === 'JPY') {
      formattedPrice = Math.round(n).toLocaleString();
    } else {
      formattedPrice = n.toFixed(2);
    }

    return showSymbol ? `${symbol}${formattedPrice}` : formattedPrice;
  };

  const formatPrice = (amount, showSymbol = true) => {
    return formatMoney(amount, currency, showSymbol);
  };

  const getProductPrice = (product, code = currency) => {
    if (!product) return null;
    const prices = product?.prices && typeof product.prices === 'object' ? product.prices : null;
    const raw = prices && Object.prototype.hasOwnProperty.call(prices, String(code)) ? prices[String(code)] : undefined;
    const n = Number(raw);
    if (Number.isFinite(n)) return n;

    if (String(code) === 'USD') {
      const legacy = Number(product?.price);
      return Number.isFinite(legacy) ? legacy : null;
    }

    return null;
  };

  const getProductCompareAtPrice = (product, code = currency) => {
    if (!product) return null;

    const map = product?.compareAtPrices && typeof product.compareAtPrices === 'object' ? product.compareAtPrices : null;
    const raw = map && Object.prototype.hasOwnProperty.call(map, String(code)) ? map[String(code)] : undefined;
    const n = Number(raw);
    if (Number.isFinite(n)) return n;

    if (String(code) === 'USD') {
      const legacy = Number(product?.compareAtPrice);
      return Number.isFinite(legacy) ? legacy : null;
    }

    return null;
  };

  const getCurrencySymbol = () => {
    return getCurrencyMeta(currency).symbol;
  };

  const getCurrencyName = () => {
    return getCurrencyMeta(currency).name;
  };

  const getAvailableCurrencies = () => {
    return Object.keys(exchangeRates).map(code => ({
      code,
      ...exchangeRates[code]
    }));
  };

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        formatPrice,
        formatMoney,
        getProductPrice,
        getProductCompareAtPrice,
        getCurrencySymbol,
        getCurrencyName,
        getAvailableCurrencies,
        exchangeRates
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};
